import { createApi } from '@reduxjs/toolkit/query/react';
import { idType, selectItemType } from '@src/types';
import { baseQuerySample } from '@store/helpers';
import { normalizeAssignmentDate } from '@component/Assignment/helpers';

const getPortalAssignmentLockHistoryRequest = (
  contractorId: idType,
  dateOfService: string,
  params: Record<string, unknown>
) => ({
  url: `/v2/search/contractor/portal/lock-history/${contractorId}`,
  method: 'GET',
  params: {
    ...params,
    dateOfService: normalizeAssignmentDate(dateOfService),
  },
});

export type assignmentFilterBodyTypes = {
  rideRequestDate?: string;
  startDate?: string;
  endDate?: string;
  includeIncompleteRides?: boolean;
  onlyWithRides?: boolean;
  state?: number | null;
  stateId?: number | null;
};

export type assignmentSearchContractorsBodyType = {
  rideRequestDate: string;
  startDate: string;
  endDate: string;
  includeIncompleteRides: boolean;
  onlyWithRides: boolean;
  query: string;
  rideQuery: string;
  state?: number | null;
  stateId?: number | null;
};

export type transmitSegmentResultType = {
  segmentId: number;
  transmitStatus: string;
  transmitIndicator?: string | null;
};

export type contractorReadyForTransmitType = {
  contractorId: number;
  readyForTransmit: boolean;
  colorStatus: 'RED' | 'GREEN' | 'NONE';
  hasTransmissionHistory: boolean;
};

export type contractorCloseoutStatusType = {
  contractorId: number;
  userName: string;
  accepted: boolean;
  locked: boolean;
};

export type assignmentSearchRidesBodyType = {
  rideRequestDate: string;
  startDate: string;
  endDate: string;
  includeIncompleteRides: boolean;
  onlyWithRides: boolean;
  contractorId?: idType | null;
  state?: number | null;
  stateId?: number | null;
  query: string;
  rideQuery: string;
};

export type contractorPortalMarketplaceRidesBodyType = {
  rideRequestDate: string;
  startDate: string;
  endDate: string;
  stateId: number | null;
  query: string;
};

export type assignmentRideLocationType = {
  address1?: string;
  address2?: string;
  businessName?: string;
  city?: string;
  state?: string;
  zipCode?: string;
};

export type assignmentRideSearchResponseType = {
  content: Array<{
    id: idType;
    rideId: idType;
    rideOrderConfirmationCode: string;
    member: string;
    service: string;
    los: string;
    pickUpTime: string;
    duration: string;
    pickUpCounty: string;
    pickUpCityZipcode: string;
    dropOfCityZipcode: string;
    miles: number;
    standingOrder: boolean;
    contractor: string;
    status: string;
    statusId?: idType;
    pickUpAddress?: string;
    pickUpLocation?: assignmentRideLocationType;
    contractorId?: number;
    transmitIndicator?: string | null;
    hasTransmittedInfoBeforeAssignToContractor?: boolean;
    includeForTransmission?: boolean;
  }>;
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
  filter: Record<string, unknown> | null;
  sortDir: 'asc' | 'desc';
  sortBy: string;
  table: string;
};

export type assignmentContractorSearchItemType = {
  id?: idType;
  contractorId?: idType;
  value?: idType;
  label?: string;
  name?: string;
  contractor?: string;
  totalElements?: number;
  count?: number;
};

export type assignmentSearchRidesParamsType = {
  page?: number;
  size?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
};

export type assignmentAssignItemType = {
  segmentIds: Array<idType>;
  contractorId: idType;
};

export type assignmentAssignBodyType = {
  assignments: Array<assignmentAssignItemType>;
};

export type assignmentStandingOrderAssignItemType = assignmentAssignItemType & {
  startDate: string;
};

export type assignmentStandingOrderAssignBodyType = {
  assignments: Array<assignmentStandingOrderAssignItemType>;
};

export type assignmentAssignResponseType = {
  updated: number;
  warnings?: Array<string>;
};

export type assignmentTransmitHistoryItemType = {
  id?: idType | string;
  rideOrderConfirmationCode?: string;
  rideDate?: string;
  scheduledPickupTime?: string;
  duration?: string;
  member?: string;
  selected?: boolean;
  disabled?: boolean;
  transmitIndicator?: string | null;
  sentTo?: string;
  email?: string;
  newCount?: number;
  n?: number;
  modifiedCount?: number;
  m?: number;
  transmittedCount?: number;
  t?: number;
  errorCount?: number;
  e?: number;
  total?: number;
  transmitType?: string;
  type?: string;
  fileId?: idType | string;
  transmitStatus?: string;
  status?: string;
  error?: string;
  errorMessage?: string;
  dateTime?: string;
  createdAt?: string;
  generatedAt?: string;
  user?: string;
  userName?: string;
  userRole?: string;
  role?: string;
  downloadUrl?: string;
  fileUrl?: string;
};

export type assignmentTransmitHistoryResponseType = {
  content?: Array<assignmentTransmitHistoryItemType>;
  rows?: Array<assignmentTransmitHistoryItemType>;
  data?: Array<assignmentTransmitHistoryItemType>;
  page?: number;
  size?: number;
  totalElements?: number;
  totalPages?: number;
  last?: boolean;
  sortDir?: 'asc' | 'desc';
  sortBy?: string;
};

export type assignmentHistoryItemType = {
  id?: idType | string;
  segmentId?: idType | string;
  rideSegmentId?: idType | string;
  member?: string;
  service?: string;
  puTime?: string;
  pickupTime?: string;
  pickUpTime?: string;
  activationTime?: string;
  scheduledPickupTime?: string;
  puCounty?: string;
  pickUpCounty?: string;
  pickupCounty?: string;
  country?: string;
  puCityZip?: string;
  puCityZipcode?: string;
  pickUpCityZipcode?: string;
  pickupCityZipcode?: string;
  picZipCode?: string;
  doCityZip?: string;
  doCityZipcode?: string;
  dropOfCityZipcode?: string;
  dropOffCityZipcode?: string;
  dropZipCode?: string;
  miles?: number | string;
  duration?: string;
  rideId?: idType | string;
  rideOrderConfirmationCode?: string;
  standingOrder?: boolean;
  st?: string;
  assignStatus?: string;
  assignmentStatus?: string;
  status?: string;
  los?: string;
  dateTime?: string;
  datetime?: string;
  auditDateTime?: string;
  eventDateTime?: string;
  createdAt?: string;
  updatedAt?: string;
  rideDate?: string;
  running?: string | number | boolean;
};

export type assignmentHistoryResponseType = {
  content?: Array<assignmentHistoryItemType>;
  rows?: Array<assignmentHistoryItemType>;
  data?: Array<assignmentHistoryItemType>;
  page?: number;
  size?: number;
  totalElements?: number;
  totalPages?: number;
  last?: boolean;
};

export type assignmentLockHistoryItemType = Record<string, unknown>;

export type assignmentLockRecordType = {
  id: number;
  contractorId: number;
  payorId: number;
  payer?: string;
  dos: string;
  action: string;
  performedByUserId: number;
  performedByUserName: string;
  performedByUserRole: string;
  performedAt: string;
  losSnapshot: string;
  fileIdentifier: string;
  totalCount: number;
  demandCount: number;
  standingCount: number;
  accCount: number;
  addCount: number;
  wcCount: number;
  gurCount: number;
  blsCount: number;
  alsCount: number;
  sctCount: number;
  fwCount: number;
  rwCount: number;
  accepted: boolean;
  locked: boolean;
  confirmWithPerson: string;
};

export type assignmentLockBodyType = {
  contractorId: number;
  dateOfService: string;
  confirmWithPerson: string;
  acceptedMoreRides: boolean;
  segmentIds: number[];
};

export type assignmentUnlockBodyType = {
  contractorId: number;
  dateOfService: string;
};

export type assignmentUnlockAllBodyType = {
  dateOfService: string;
};

export type assignmentLockPopupBodyType = {
  segmentIds: number[];
};

export type assignmentLockPopupCountsType = {
  total: number;
  demand: number;
  standing: number;
  acc: number;
  add: number;
  wc: number;
  gur: number;
  bls: number;
  als: number;
  sct: number;
  fw: number;
  rw: number;
};

export type assignmentLockHistoryResponseType = {
  content?: Array<assignmentLockHistoryItemType>;
  rows?: Array<assignmentLockHistoryItemType>;
  data?: Array<assignmentLockHistoryItemType>;
  page?: number;
  size?: number;
  totalElements?: number;
  totalPages?: number;
  last?: boolean;
};

export type portalAssignmentLockArgs = {
  contractorId: number;
  dateOfService: string;
};

export type portalAssignmentLockBody = portalAssignmentLockArgs & {
  payorId: number;
  acceptedMoreRides?: boolean;
};

export type portalAssignmentPayor = selectItemType & { auxConEnabled: boolean };

export const assignmentApi = createApi({
  reducerPath: 'Assignment',
  tagTypes: ['Assignment', 'PortalAssignmentLock'],
  baseQuery: baseQuerySample,
  endpoints: (builder) => ({
    searchAssignmentRides: builder.mutation<
      assignmentRideSearchResponseType,
      {
        body: assignmentSearchRidesBodyType;
        params?: assignmentSearchRidesParamsType;
      }
    >({
      query: ({ body, params = {} }) => ({
        url: `/v2/search/assigment/search-rides`,
        method: 'POST',
        body,
        params: {
          page: 0,
          size: 20,
          ...('sortBy' in params && params.sortBy
            ? { sortBy: params.sortBy }
            : {}),
          ...(params.sortDir ? { sortDir: params.sortDir } : {}),
          ...(typeof params.page === 'number' ? { page: params.page } : {}),
          ...(typeof params.size === 'number' ? { size: params.size } : {}),
        },
      }),
    }),
    searchContractorPortalMarketplaceRides: builder.mutation<
      assignmentRideSearchResponseType,
      {
        body: assignmentSearchRidesBodyType;
        params?: assignmentSearchRidesParamsType;
      }
    >({
      query: ({ body, params = {} }) => ({
        url: `/v2/search/contractor/portal/marketplace-rides`,
        method: 'POST',
        body: <contractorPortalMarketplaceRidesBodyType>{
          rideRequestDate: body.rideRequestDate,
          startDate: body.startDate,
          endDate: body.endDate,
          stateId: body.stateId ?? body.state ?? null,
          query: body.rideQuery || body.query,
        },
        params: {
          page: 0,
          size: 20,
          ...('sortBy' in params && params.sortBy
            ? { sortBy: params.sortBy }
            : {}),
          ...(params.sortDir ? { sortDir: params.sortDir } : {}),
          ...(typeof params.page === 'number' ? { page: params.page } : {}),
          ...(typeof params.size === 'number' ? { size: params.size } : {}),
        },
      }),
    }),
    getContractorPortalMarketplaceRidesCount: builder.query({
      query: (dateOfService) => ({
        url: `/v2/search/contractor/portal/marketplace-rides/count`,
        params: { dateOfService },
      }),
      transformResponse: (response: number | { count?: number }) =>
        typeof response === 'number' ? response : (response?.count ?? 0),
    }),
    searchAssignmentTransmitRides: builder.mutation<
      assignmentRideSearchResponseType,
      | (assignmentSearchRidesBodyType & { searchAll: boolean })
      | {
          body: assignmentSearchRidesBodyType;
          params?: assignmentSearchRidesParamsType;
          isContractorPortal?: boolean;
        }
    >({
      query: (args) => {
        const isAssignmentGridRequest = 'body' in args;
        const isContractorPortal =
          isAssignmentGridRequest && args.isContractorPortal === true;

        return {
          url: isContractorPortal
            ? `/v2/search/contractor/portal/combined-search-rides`
            : `/v2/search/assigment/combined-search-rides`,
          method: 'POST',
          body: isAssignmentGridRequest
            ? args.body
            : {
                ...args,
                transmittedInfo: true,
              },
          params: isAssignmentGridRequest
            ? {
                page: 0,
                size: 20,
                sortBy: 'rideId',
                sortDir: 'asc',
                ...args.params,
              }
            : {
                page: 0,
                sortBy: 'rideOrderConfirmationCode',
                sortDir: 'asc',
              },
        };
      },
    }),
    searchAssignmentContractors: builder.mutation<
      | Array<assignmentContractorSearchItemType>
      | { content?: Array<assignmentContractorSearchItemType> },
      assignmentSearchContractorsBodyType
    >({
      query: (body) => ({
        url: `/v2/search/assigment/search-contractor`,
        method: 'POST',
        body,
      }),
    }),
    assignCombinedSearch: builder.mutation({
      query: ({ body, params = {} }) => ({
        url: `/v2/search/assigment/combined-search`,
        method: 'POST',
        body,
        params: {
          sortBy: 'rideId',
          sortDir: 'asc',
          ...params,
        },
      }),
    }),

    assignCombinedSearchContractors: builder.mutation({
      query: ({ body }) => ({
        url: `/v2/search/assigment/combined-search-contractors`,
        method: 'POST',
        body: {
          includeRideshareContractors: true,
          ...body,
        },
      }),
    }),

    // getSegmentDetailsById: builder.query({
    //   query: ({ id }) => ({
    //     url: `/v2/rides/assignment/segment/${id}`,
    //   }),
    // }),
    getContractorCloseoutStatus: builder.query<
      contractorCloseoutStatusType,
      { contractorId: idType; dateOfService: string }
    >({
      query: ({ contractorId, dateOfService }) => ({
        url: `/v1/search/contractor/closeout/${contractorId}`,
        params: { dateOfService },
      }),
    }),
    assignRides: builder.mutation<
      assignmentAssignResponseType,
      assignmentAssignBodyType
    >({
      query: (body) => ({
        url: `/v2/rides/assignment/assign`,
        method: 'PUT',
        body,
      }),
    }),
    assignStandingOrderRides: builder.mutation<
      assignmentAssignResponseType,
      assignmentStandingOrderAssignBodyType
    >({
      query: (body) => ({
        url: `/v2/rides/assignment/assign-so`,
        method: 'PUT',
        body,
      }),
    }),
    saveAssignedRides: builder.mutation<unknown, assignmentAssignItemType>({
      query: (body) => ({
        url: `/v2/search/assigment/assign`,
        method: 'PUT',
        body,
      }),
    }),
    getTransmitHistory: builder.mutation({
      query: ({ contractorId, params, isContractorPortal = false }) => ({
        url: isContractorPortal
          ? `/v2/search/contractor/portal/transmit-history/${contractorId}`
          : `/v1/search/transmit/segments/${contractorId}`,
        method: isContractorPortal ? 'GET' : 'POST',
        params: isContractorPortal
          ? {
              ...params,
              dateOfService: normalizeAssignmentDate(params.dateOfService),
            }
          : params,
      }),
    }),
    rejectPortalRides: builder.mutation<
      assignmentAssignResponseType,
      { segmentIds: number[]; reasonId?: number }
    >({
      query: (body) => ({
        url: '/v1/rides/contractor/portal/assignment/reject',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['PortalAssignmentLock'],
    }),
    claimPortalRides: builder.mutation<
      assignmentAssignResponseType,
      { segmentIds: number[] }
    >({
      query: (body) => ({
        url: '/v1/rides/contractor/portal/assignment/claim',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['PortalAssignmentLock'],
    }),

    downloadPortalSegments: builder.mutation({
      query: (body) => ({
        url: '/v1/rides/contractor/portal/transmit/download',
        method: 'POST',
        body: {
          ...body,
          dateOfService: normalizeAssignmentDate(body.dateOfService),
        },
        responseHandler: async (response) => response.blob(),
      }),
    }),

    getContractorsWithNonTransmittedSegments: builder.mutation<
      boolean,
      { contractorIds: number[]; dateOfService: string }
    >({
      query: ({ contractorIds, dateOfService }) => ({
        url: `/v1/search/transmit/contractors/enabled`,
        method: 'POST',
        params: { dateOfService },
        body: contractorIds,
      }),
    }),
    getContractorsReadyForTransmit: builder.mutation<
      contractorReadyForTransmitType[],
      { contractorIds: number[]; dateOfService: string }
    >({
      query: ({ contractorIds, dateOfService }) => ({
        url: `/v1/search/transmit/segments/ready-for-transmit`,
        method: 'POST',
        params: { dateOfService },
        body: contractorIds,
      }),
    }),
    getAssignmentHistory: builder.mutation({
      query: ({ contractorId, params, isContractorPortal = false }) => ({
        url: isContractorPortal
          ? `/v2/search/contractor/portal/history/${contractorId}`
          : `/v2/rides/assignment/history/${contractorId}`,
        method: isContractorPortal ? 'GET' : 'POST',
        params,
      }),
    }),
    getAssignmentLockHistory: builder.mutation({
      query: ({
        contractorId,
        params: { dateOfService, ...params },
        isContractorPortal = false,
      }) =>
        isContractorPortal
          ? getPortalAssignmentLockHistoryRequest(
              contractorId,
              dateOfService,
              params
            )
          : {
              url: `/v1/search/assignment/lock/lock-history/${contractorId}`,
              method: 'POST',
              params,
              body: { dateOfService },
            },
    }),
    getPortalAssignmentPayors: builder.query<portalAssignmentPayor[], number>({
      async queryFn(_contractorId, _api, _extra, baseQuery) {
        const result = await baseQuery({
          url: '/v1/rides/contractor/portal/assignment/lock/payors',
        });
        if (result.error) return { error: result.error };
        const payors = result.data as selectItemType[];

        return {
          data: payors.map((payor, index) => ({
            ...payor,
            // auxConEnabled: flags[index].data === true,
            auxConEnabled: true,
          })),
        };
      },
    }),

    getPortalAssignmentLocks: builder.query<
      assignmentLockRecordType[],
      portalAssignmentLockArgs
    >({
      async queryFn({ contractorId, dateOfService }, _api, _extra, baseQuery) {
        const records: assignmentLockRecordType[] = [];
        let page = 0;
        while (true) {
          const result = await baseQuery(
            getPortalAssignmentLockHistoryRequest(contractorId, dateOfService, {
              page,
              size: 100,
              sortBy: 'performedAt',
              sortDir: 'desc',
            })
          );
          if (result.error) return { error: result.error };
          const payload = result.data as assignmentLockHistoryResponseType;
          records.push(
            ...((payload.content ?? []) as assignmentLockRecordType[])
          );
          if (
            payload.last ||
            !payload.content?.length ||
            page + 1 >= (payload.totalPages ?? 1)
          )
            break;
          page += 1;
        }
        return { data: records };
      },
      providesTags: (_result, _error, args) => [
        {
          type: 'PortalAssignmentLock',
          id: `${args.contractorId}:${args.dateOfService}`,
        },
      ],
    }),

    getPortalAssignmentLockPopup: builder.query<
      assignmentLockPopupCountsType,
      portalAssignmentLockBody
    >({
      query: ({ payorId, dateOfService }) => ({
        url: '/v1/rides/contractor/portal/assignment/lock/pop-up',
        method: 'POST',
        body: {
          payorId,
          dateOfService: normalizeAssignmentDate(dateOfService),
        },
      }),
    }),
    savePortalAssignmentLock: builder.mutation<
      assignmentLockRecordType,
      portalAssignmentLockBody & { userId: number; unlock?: boolean }
    >({
      query: ({ payorId, dateOfService, acceptedMoreRides, unlock }) => ({
        url: `/v1/rides/contractor/portal/assignment/lock${unlock ? '/unlock' : ''}`,
        method: 'POST',
        body: {
          payorId,
          dateOfService: normalizeAssignmentDate(dateOfService),
          ...(unlock ? {} : { acceptedMoreRides }),
        },
      }),
      async onQueryStarted(
        { contractorId, dateOfService },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            assignmentApi.util.updateQueryData(
              'getPortalAssignmentLocks',
              { contractorId, dateOfService },
              (records) => {
                records.unshift(data);
              }
            )
          );
        } catch {
          // A failed save leaves the current lock state intact.
        }
      },
      invalidatesTags: (_result, error, args) =>
        error
          ? []
          : [
              {
                type: 'PortalAssignmentLock',
                id: `${args.contractorId}:${args.dateOfService}`,
              },
            ],
    }),
    getAssignmentLockPopup: builder.mutation<
      assignmentLockPopupCountsType,
      assignmentLockPopupBodyType
    >({
      query: (body) => ({
        url: `/v1/rides/assignment/lock/pop-up`,
        method: 'POST',
        body,
      }),
    }),
    setSingleTransmit: builder.mutation<
      transmitSegmentResultType[],
      {
        body: {
          segmentIds: number[];
          dateOfService: string;
          fullSegments: boolean;
        };
        params: Record<string, any>;
      }
    >({
      query: ({ params, body }) => ({
        url: `/v1/rides/transmit/segments/transmit`,
        method: 'POST',
        params,
        body,
      }),
    }),
    setMassTransmit: builder.mutation({
      query: ({
        method,
        body,
      }: {
        method: string;
        body: {
          contractorIds: number[];
          dateOfService: string;
        };
      }) => ({
        url: `/v1/rides/transmit/all`,
        method: 'POST',
        params: {
          method,
        },
        body,
      }),
    }),
    getTransmitMethods: builder.query({
      query: () => ({
        url: `/v1/rides/transmit/methods`,
      }),
    }),
    getAssignmentPortalSegmentInfo: builder.query({
      query: (segmentId: number) => ({
        url: `/v2/rides/assignment/contractor-portal/segment/${segmentId}`,
      }),
    }),
    unlockAssignment: builder.mutation<
      assignmentLockRecordType,
      assignmentUnlockBodyType
    >({
      query: (body) => ({
        url: `/v1/rides/assignment/lock/unlock`,
        method: 'POST',
        body,
      }),
    }),
    unlockAllAssignments: builder.mutation<
      Array<assignmentLockRecordType>,
      assignmentUnlockAllBodyType
    >({
      query: (body) => ({
        url: `/v1/rides/assignment/lock/unlock-all`,
        method: 'POST',
        body,
      }),
    }),
    lockAssignment: builder.mutation<
      assignmentLockRecordType,
      assignmentLockBodyType
    >({
      query: (body) => ({
        url: `/v1/rides/assignment/lock`,
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useRejectPortalRidesMutation,
  useClaimPortalRidesMutation,
  useGetPortalAssignmentPayorsQuery,
  useGetPortalAssignmentLocksQuery,
  useGetPortalAssignmentLockPopupQuery,
  useSavePortalAssignmentLockMutation,
  useSearchAssignmentRidesMutation,
  useSearchAssignmentTransmitRidesMutation,
  useSearchContractorPortalMarketplaceRidesMutation,
  useGetContractorPortalMarketplaceRidesCountQuery,
  useSearchAssignmentContractorsMutation,
  useAssignRidesMutation,
  useAssignStandingOrderRidesMutation,
  useSaveAssignedRidesMutation,
  useGetTransmitHistoryMutation,
  useGetContractorsWithNonTransmittedSegmentsMutation,
  useGetContractorsReadyForTransmitMutation,
  useGetAssignmentHistoryMutation,
  useGetAssignmentLockHistoryMutation,
  useGetAssignmentLockPopupMutation,
  useAssignCombinedSearchMutation,
  useAssignCombinedSearchContractorsMutation,
  useSetSingleTransmitMutation,
  useSetMassTransmitMutation,
  useLazyGetTransmitMethodsQuery,
  useLazyGetContractorCloseoutStatusQuery,
  useUnlockAssignmentMutation,
  useUnlockAllAssignmentsMutation,
  useLockAssignmentMutation,
  useLazyGetAssignmentPortalSegmentInfoQuery,
  useDownloadPortalSegmentsMutation,
} = assignmentApi;
